import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { strategicPerformanceStateAPI } from '../utils/apiService';

type OldStrategicPerformanceState = Record<string, unknown>;

interface OldStrategicPerformanceStateContextValue {
  state: OldStrategicPerformanceState;
  updateKey: (key: string, value: unknown) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const OldStrategicPerformanceStateContext =
  createContext<OldStrategicPerformanceStateContextValue | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const OldStrategicPerformanceStateProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<OldStrategicPerformanceState>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadState = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await strategicPerformanceStateAPI.getState();
        setState(data);
      } catch (err: any) {
        console.error('Failed to load strategic performance state:', err);
        setError(err.response?.data?.error || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadState();
  }, []);

  const updateKey = useCallback(async (key: string, value: unknown) => {
    setState(prev => ({
      ...prev,
      [key]: value,
    }));

    try {
      await strategicPerformanceStateAPI.setKey(key, value);
    } catch (err) {
      console.error('Failed to persist strategic performance state:', err);
      // No rollback to keep UI responsive; consider refetching if needed
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      updateKey,
      loading,
      error,
    }),
    [state, updateKey, loading, error]
  );

  return (
    <OldStrategicPerformanceStateContext.Provider value={contextValue}>
      {children}
    </OldStrategicPerformanceStateContext.Provider>
  );
};

export const useOldStrategicPerformanceStateContext = () => {
  const context = useContext(OldStrategicPerformanceStateContext);
  if (!context) {
    throw new Error(
      'useOldStrategicPerformanceStateContext must be used within OldStrategicPerformanceStateProvider'
    );
  }
  return context;
};

